import { useState, useRef, useEffect } from 'react';
import {
  Folder,
  FolderOpen,
  Star,
  Box,
  Package,
  Layers,
  Grid3x3,
  Briefcase,
  Archive,
  BookOpen,
  Bookmark,
  Heart,
  Zap,
  Sparkles,
  Target,
  Tag,
  Wrench,
  Cog,
  ChevronDown,
} from 'lucide-react';

// Curated list of icons suitable for collections
export const iconOptions = [
  { name: 'folder', Icon: Folder, label: 'Folder' },
  { name: 'folder-open', Icon: FolderOpen, label: 'Folder Open' },
  { name: 'star', Icon: Star, label: 'Star' },
  { name: 'box', Icon: Box, label: 'Box' },
  { name: 'package', Icon: Package, label: 'Package' },
  { name: 'layers', Icon: Layers, label: 'Layers' },
  { name: 'grid-3x3', Icon: Grid3x3, label: 'Grid' },
  { name: 'briefcase', Icon: Briefcase, label: 'Briefcase' },
  { name: 'archive', Icon: Archive, label: 'Archive' },
  { name: 'book-open', Icon: BookOpen, label: 'Book' },
  { name: 'bookmark', Icon: Bookmark, label: 'Bookmark' },
  { name: 'heart', Icon: Heart, label: 'Heart' },
  { name: 'zap', Icon: Zap, label: 'Zap' },
  { name: 'sparkles', Icon: Sparkles, label: 'Sparkles' },
  { name: 'target', Icon: Target, label: 'Target' },
  { name: 'tag', Icon: Tag, label: 'Tag' },
  { name: 'wrench', Icon: Wrench, label: 'Wrench' },
  { name: 'cog', Icon: Cog, label: 'Settings' },
];

interface CollectionIconPickerProps {
  selectedIcon?: string | null;
  onSelect: (iconName: string) => void;
  color?: string | null;
  label?: string;
}

export function CollectionIconPicker({
  selectedIcon,
  onSelect,
  color,
  label,
}: CollectionIconPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const selectedOption = iconOptions.find((opt) => opt.name === selectedIcon) || iconOptions[0];
  const SelectedIcon = selectedOption.Icon;

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleSelect = (iconName: string) => {
    onSelect(iconName);
    setIsOpen(false);
  };

  return (
    <div className="w-full">
      {label && (
        <label className="mb-1.5 block text-sm font-medium text-foreground">{label}</label>
      )}
      <div className="relative">
        {/* Dropdown Button */}
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="block w-full appearance-none rounded-md border bg-background-subtle text-foreground
            focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary
            h-10 px-3 text-sm border-border
            flex items-center justify-between"
        >
          <div className="flex items-center gap-2">
            <SelectedIcon className="h-4 w-4" style={{ color: color || undefined }} />
            <span>{selectedOption.label}</span>
          </div>
          <ChevronDown className="h-4 w-4 text-foreground-muted" />
        </button>

        {/* Dropdown Menu - Fixed positioning to break out of modal */}
        {isOpen && (
          <div
            ref={dropdownRef}
            className="fixed z-[100] mt-1 rounded-md border border-border bg-background shadow-lg max-h-64 overflow-y-auto"
            style={{
              top: `${dropdownPosition.top + 4}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
            }}
          >
            {iconOptions.map(({ name, Icon, label: optionLabel }) => (
              <button
                key={name}
                type="button"
                onClick={() => handleSelect(name)}
                className={`
                  w-full flex items-center gap-2 px-3 py-2 text-sm
                  hover:bg-background-subtle transition-colors
                  ${selectedIcon === name ? 'bg-primary/10 text-primary' : 'text-foreground'}
                `}
              >
                <Icon className="h-4 w-4" style={{ color: selectedIcon === name && color ? color : undefined }} />
                <span>{optionLabel}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
